/**
 * ThreadToolbar — reading-pane action bar with progressive overflow menu.
 */

import { h, Fragment } from 'preact'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks'
import { tagTextColor, threadTagIds, threadUiState } from '../../modules/email/email-helpers.js'
import {
  computeOverflowIds,
  coalesceEndGroupOverflowIfNeeded,
  OVERFLOW_BUTTON_WIDTH,
} from '../../modules/email/thread-toolbar-overflow.js'
import { PersonAvatar } from './PersonAvatar.jsx'

// Fix typo - I used OUTFLOW instead of OVERFLOW in import
const OVERFLOW_BTN_WIDTH = OVERFLOW_BUTTON_WIDTH

const EMAIL_STATUSES = ['open', 'inprogress', 'waiting_for_customer', 'closed']

export function ThreadToolbar({ thread, store, i18n, folders, peopleWithUser, onMobileBack }) {
  const toolbarRef = useRef(null)
  const trackRef = useRef(null)
  const [overflowIds, setOverflowIds] = useState(() => new Set())

  const recomputeOverflow = useCallback(() => {
    const track = trackRef.current
    const toolbar = toolbarRef.current
    if (!track || !toolbar) return

    const endEl = toolbar.querySelector('.email-thread-toolbar-end')
    const gap = 4

    if (window.matchMedia('(max-width: 767.98px)').matches) {
      toolbar.querySelectorAll('[data-toolbar-item]').forEach(el => {
        el.classList.remove('email-thread-toolbar-item--hidden')
      })
      setOverflowIds(new Set())
      return
    }

    toolbar.querySelectorAll('[data-toolbar-item]').forEach(el => {
      el.classList.remove('email-thread-toolbar-item--hidden')
    })

    const itemEls = track.querySelectorAll('[data-toolbar-item]')
    const items = []
    itemEls.forEach(el => {
      const id = el.getAttribute('data-toolbar-item')
      const overflowable = el.getAttribute('data-toolbar-overflowable') !== 'false'
      items.push({ id, width: el.offsetWidth + gap, overflowable })
    })

    // Measure against the track's real width — toolbar.clientWidth over-estimates and
    // leaves items clipped by overflow-hidden without ever showing the ⋮ menu.
    let available = track.clientWidth
    let next = computeOverflowIds(items, available)

    const trackClipped = track.scrollWidth > track.clientWidth + 1
    if (next.size === 0 && trackClipped) {
      available = Math.max(0, track.clientWidth - OVERFLOW_BTN_WIDTH - gap)
      next = computeOverflowIds(items, available)
    }

    if (next.size > 0) {
      available = Math.max(0, track.clientWidth - OVERFLOW_BTN_WIDTH - gap)
      next = computeOverflowIds(items, available)
    }

    // Keep status + assign visible whenever the end group fits; only move them into ⋮ on very tight toolbars.
    const statusEl = endEl?.querySelector('[data-toolbar-item="status"]')
    const assignEl = endEl?.querySelector('[data-toolbar-item="assign"]')
    const statusWidth = statusEl?.offsetWidth || 0
    const assignWidth = assignEl?.offsetWidth || 0
    const endGroupWidth = statusWidth + assignWidth
      + (statusWidth > 0 && assignWidth > 0 ? gap : 0)
      + (next.size > 0 ? OVERFLOW_BTN_WIDTH + gap : 0)
    const endBudget = Math.max(0, toolbar.clientWidth - track.offsetWidth)
    next = coalesceEndGroupOverflowIfNeeded(next, endGroupWidth, endBudget)

    itemEls.forEach(el => {
      const id = el.getAttribute('data-toolbar-item')
      el.classList.toggle('email-thread-toolbar-item--hidden', next.has(id))
    })

    endEl?.querySelectorAll('[data-toolbar-item]').forEach(el => {
      const id = el.getAttribute('data-toolbar-item')
      el.classList.toggle('email-thread-toolbar-item--hidden', next.has(id))
    })

    setOverflowIds(prev => {
      if (prev.size === next.size && [...prev].every(id => next.has(id))) return prev
      return next
    })
  }, [])

  useLayoutEffect(() => {
    recomputeOverflow()
  }, [thread.id, store.canEdit.value, folders.length, recomputeOverflow])

  useEffect(() => {
    const toolbar = toolbarRef.current
    if (!toolbar || typeof ResizeObserver === 'undefined') return

    const ro = new ResizeObserver(() => recomputeOverflow())
    ro.observe(toolbar)
    return () => ro.disconnect()
  }, [recomputeOverflow])

  return (
    <div
      ref={toolbarRef}
      id={`email_${thread.id}_thread_toolbar`}
      class="email-thread-toolbar d-flex align-items-center gap-1 px-2 py-1 border-bottom"
      data-turbo="false"
    >
      <div ref={trackRef} class="email-thread-toolbar-track d-flex align-items-center gap-1 flex-grow-1 min-w-0 overflow-hidden">
        <ToolbarItem id="back" overflowable={false}>
          <button
            class={`btn btn-sm email-thread-btn-icon me-1 ${store.isThreadOnlyView.value ? '' : 'd-md-none'}`}
            onClick={onMobileBack}
            title={i18n?.t('helpers.back')}
          >
            <i class="bi bi-arrow-left" />
          </button>
        </ToolbarItem>

        <ToolbarItem id="reply" overflowable={false}>
          <button class="btn btn-sm email-thread-btn-icon" title={i18n?.t('emails.reply')} onClick={() => store.openCompose('reply', thread.id)}>
            <i class="bi bi-reply" />
          </button>
        </ToolbarItem>

        <ToolbarItem id="reply-all">
          <button class="btn btn-sm email-thread-btn-icon d-none d-md-inline-flex" title={i18n?.t('emails.reply_all')} onClick={() => store.openCompose('reply_all', thread.id)}>
            <i class="bi bi-reply-all" />
          </button>
        </ToolbarItem>

        <ToolbarItem id="forward" overflowable={false}>
          <button class="btn btn-sm email-thread-btn-icon" title={i18n?.t('emails.forward')} onClick={() => store.openCompose('forward', thread.id)}>
            <i class="bi bi-forward" />
          </button>
        </ToolbarItem>

        <ToolbarItem id="internal-note">
          <button class="btn btn-sm email-thread-btn-icon d-none d-md-inline-flex" title={i18n?.t('emails.add_internal_note')} onClick={store.showCommentForm}>
            <i class="bi bi-sticky" />
          </button>
        </ToolbarItem>

        <ToolbarItem id="divider-state" overflowable={false}>
          <div class="vr mx-1 d-none d-md-block" />
        </ToolbarItem>

        <ToolbarItem id="thread-state" overflowable={false}>
          <ThreadStateButtons thread={thread} store={store} i18n={i18n} />
        </ToolbarItem>

        <ToolbarItem id="mark-unread">
          <button class="btn btn-sm email-thread-btn-icon d-none d-md-inline-flex" title={i18n?.t('emails.mark_unread')}
                  onClick={() => store.updateEmailState(thread.id, 'unread')}>
            <i class="bi bi-envelope" />
          </button>
        </ToolbarItem>

        <ToolbarItem id="star">
          <StarButton thread={thread} store={store} i18n={i18n} />
        </ToolbarItem>

        <ToolbarItem id="move-folder">
          <MoveFolderDropdown thread={thread} store={store} i18n={i18n} folders={folders} />
        </ToolbarItem>

        <ToolbarItem id="divider-tags" overflowable={false}>
          <div class="vr mx-1 d-none d-md-block" />
        </ToolbarItem>

        <ToolbarItem id="tag-picker">
          <TagPickerDropdown thread={thread} store={store} i18n={i18n} />
        </ToolbarItem>

        <ToolbarItem id="divider-tasks" overflowable={false}>
          <div class="vr mx-1 d-none d-md-block" />
        </ToolbarItem>

        {store.canEdit.value && (
          <>
            <ToolbarItem id="task-new">
              <button
                type="button"
                class="btn btn-sm email-thread-btn-icon d-none d-md-inline-flex"
                title={i18n?.t('emails.new_task_tooltip')}
                onClick={() => store.openTaskCreateModal(thread.id)}
              >
                <i class="bi bi-list-task" />
              </button>
            </ToolbarItem>
            <ToolbarItem id="task-link">
              <button
                type="button"
                class="btn btn-sm email-thread-btn-icon d-none d-md-inline-flex"
                title={i18n?.t('emails.link_to_task')}
                onClick={() => store.openTaskLinkPicker(thread.id)}
              >
                <i class="bi bi-link-45deg" />
              </button>
            </ToolbarItem>
          </>
        )}

      </div>

      <div class="email-thread-toolbar-end ms-auto d-flex align-items-center gap-1 flex-shrink-0">
        {overflowIds.size > 0 && (
          <ToolbarOverflowMenu
            overflowIds={overflowIds}
            thread={thread}
            store={store}
            i18n={i18n}
            folders={folders}
            peopleWithUser={peopleWithUser}
          />
        )}

        <MobileMoreMenu thread={thread} store={store} i18n={i18n} folders={folders} />

        <ToolbarItem id="status">
          <StatusDropdown thread={thread} store={store} i18n={i18n} />
        </ToolbarItem>

        <ToolbarItem id="assign">
          <AssignDropdown thread={thread} store={store} i18n={i18n} peopleWithUser={peopleWithUser} />
        </ToolbarItem>
      </div>
    </div>
  )
}

function ToolbarItem({ id, overflowable = true, children }) {
  return (
    <div
      class="email-thread-toolbar-item flex-shrink-0"
      data-toolbar-item={id}
      data-toolbar-overflowable={overflowable ? 'true' : 'false'}
    >
      {children}
    </div>
  )
}

function ToolbarOverflowMenu({ overflowIds, thread, store, i18n, folders, peopleWithUser }) {
  return (
    <div class="dropdown email-thread-toolbar-overflow flex-shrink-0">
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        data-bs-toggle="dropdown"
        data-bs-popper-config='{"strategy":"fixed"}'
        data-bs-auto-close="outside"
        title={i18n?.t('helpers.more_actions') || 'More actions'}
        aria-label={i18n?.t('helpers.more_actions') || 'More actions'}
      >
        <i class="bi bi-three-dots-vertical" />
      </button>
      <ul class="dropdown-menu dropdown-menu-end shadow-sm email-thread-toolbar-dropdown-menu">
        {overflowIds.has('reply-all') && (
          <li>
            <button type="button" class="dropdown-item d-flex align-items-center gap-2 small"
               onClick={() => { store.openCompose('reply_all', thread.id) }}>
              <i class="bi bi-reply-all" />
              {i18n?.t('emails.reply_all')}
            </button>
          </li>
        )}
        {overflowIds.has('internal-note') && (
          <li>
            <button type="button" class="dropdown-item d-flex align-items-center gap-2 small"
               onClick={() => { store.showCommentForm() }}>
              <i class="bi bi-sticky" />
              {i18n?.t('emails.add_internal_note')}
            </button>
          </li>
        )}
        {overflowIds.has('mark-unread') && (
          <li>
            <button type="button" class="dropdown-item d-flex align-items-center gap-2 small"
               onClick={() => { store.updateEmailState(thread.id, 'unread') }}>
              <i class="bi bi-envelope" />
              {i18n?.t('emails.mark_unread')}
            </button>
          </li>
        )}
        {overflowIds.has('star') && (
          <StarOverflowItem thread={thread} store={store} i18n={i18n} />
        )}
        {overflowIds.has('move-folder') && (
          <MoveFolderOverflowSection thread={thread} store={store} i18n={i18n} folders={folders} />
        )}
        {overflowIds.has('tag-picker') && (
          <TagOverflowSection thread={thread} store={store} i18n={i18n} />
        )}
        {(overflowIds.has('task-new') || overflowIds.has('task-link')) && store.canEdit.value && (
          <>
            <li><hr class="dropdown-divider my-1" /></li>
            {overflowIds.has('task-new') && (
              <li>
                <button
                  type="button"
                  class="dropdown-item d-flex align-items-center gap-2 small"
                  onClick={() => store.openTaskCreateModal(thread.id)}
                >
                  <i class="bi bi-list-task" />
                  {i18n?.t('emails.new_task_tooltip')}
                </button>
              </li>
            )}
            {overflowIds.has('task-link') && (
              <li>
                <button
                  type="button"
                  class="dropdown-item d-flex align-items-center gap-2 small"
                  onClick={() => store.openTaskLinkPicker(thread.id)}
                >
                  <i class="bi bi-link-45deg" />
                  {i18n?.t('emails.link_to_task')}
                </button>
              </li>
            )}
          </>
        )}
        {overflowIds.has('status') && (
          <StatusOverflowSection thread={thread} store={store} i18n={i18n} />
        )}
        {overflowIds.has('assign') && (
          <AssignOverflowSection thread={thread} store={store} i18n={i18n} peopleWithUser={peopleWithUser} />
        )}
      </ul>
    </div>
  )
}

function ThreadStateButtons({ thread, store, i18n }) {
  const emailRow = store.emails.value.find(e => e.id === thread.id)
  const state = threadUiState(thread, emailRow)
  const isTrashed  = state.trashed
  const isArchived = state.archived
  const isSpam     = !!(state.spam || thread.spam || thread.spam_at)

  if (isTrashed || isArchived || isSpam) {
    return (
      <>
        <button class="btn btn-sm email-thread-btn-icon email-thread-btn-icon--success" title={i18n?.t(isSpam ? 'emails.not_spam' : 'emails.restore_to_inbox')}
                onClick={() => store.updateEmailState(thread.id, isSpam ? 'not_spam' : 'restore')}>
          <i class="bi bi-arrow-counterclockwise" />
        </button>
        {isTrashed && (
          <button
            class="btn btn-sm email-thread-btn-icon email-thread-btn-icon--danger"
            title={i18n?.t('emails.permanent_delete')}
            onClick={() => {
              if (confirm(i18n?.t('emails.permanent_delete_confirm'))) {
                store.updateEmailState(thread.id, 'delete')
              }
            }}
          >
            <i class="bi bi-trash3" />
          </button>
        )}
      </>
    )
  }

  return (
    <>
      <button class="btn btn-sm email-thread-btn-icon" title={i18n?.t('emails.archive')}
              onClick={() => store.updateEmailState(thread.id, 'archive')}>
        <i class="bi bi-archive" />
      </button>
      <button class="btn btn-sm email-thread-btn-icon" title={i18n?.t('emails.mark_spam')}
              onClick={() => store.updateEmailState(thread.id, 'spam')}>
        <i class="bi bi-exclamation-octagon" />
      </button>
      <button class="btn btn-sm email-thread-btn-icon" title={i18n?.t('emails.trash')}
              onClick={() => store.updateEmailState(thread.id, 'trash')}>
        <i class="bi bi-trash" />
      </button>
    </>
  )
}

function StatusDropdown({ thread, store, i18n }) {
  const statusColor = { open: 'text-secondary', inprogress: 'text-warning', waiting_for_customer: 'text-info', closed: 'text-success' }

  return (
    <div class="dropdown">
      <button class="btn btn-sm btn-outline-secondary dropdown-toggle d-flex align-items-center gap-1" data-bs-toggle="dropdown" data-bs-popper-config='{"strategy":"fixed"}'>
        <i class={`bi bi-circle-fill ${statusColor[thread.status] || 'text-secondary'}`} style="font-size:0.5rem" />
        <span class="d-none d-lg-inline">{i18n?.t(`emails.status.${thread.status}`)}</span>
      </button>
      <ul class="dropdown-menu dropdown-menu-end shadow-sm">
        <StatusMenuItems thread={thread} store={store} i18n={i18n} statusColor={statusColor} />
      </ul>
    </div>
  )
}

function StatusMenuItems({ thread, store, i18n, statusColor }) {
  return EMAIL_STATUSES.map(s => (
    <li key={s}>
      <button type="button"
        class={`dropdown-item d-flex align-items-center gap-2 ${thread.status === s ? 'fw-semibold' : ''}`}
        onClick={() => { store.changeEmailStatus(thread.id, s) }}
      >
        <i class={`bi bi-circle-fill ${statusColor[s]}`} style="font-size:0.45rem" />
        {i18n?.t(`emails.status.${s}`)}
        {thread.status === s && <i class="bi bi-check ms-auto text-primary" />}
      </button>
    </li>
  ))
}

function StatusOverflowSection({ thread, store, i18n }) {
  const statusColor = { open: 'text-secondary', inprogress: 'text-warning', waiting_for_customer: 'text-info', closed: 'text-success' }
  return (
    <>
      <li><hr class="dropdown-divider my-1" /></li>
      <li><span class="dropdown-item-text text-muted small">{i18n?.t('emails.filter.status')}</span></li>
      <StatusMenuItems thread={thread} store={store} i18n={i18n} statusColor={statusColor} />
    </>
  )
}

function AssignDropdown({ thread, store, i18n, peopleWithUser }) {
  const responsible = thread.responsible

  return (
    <div class="dropdown">
      <button class="btn btn-sm btn-outline-secondary dropdown-toggle d-flex align-items-center gap-1" data-bs-toggle="dropdown" data-bs-popper-config='{"strategy":"fixed"}'>
        {responsible
          ? (
            <>
              <PersonAvatar avatarUrl={responsible.avatar_url} name={responsible.name} size={20} />
              <span class="d-none d-lg-inline">{responsible.name}</span>
            </>
          )
          : (
            <>
              <i class="bi bi-person-plus" />
              <span class="d-none d-lg-inline">{i18n?.t('emails.assign')}</span>
            </>
          )
        }
      </button>
      <ul class="dropdown-menu dropdown-menu-end shadow-sm" style="min-width:200px;max-height:60vh;overflow-y:auto">
        <AssignMenuItems thread={thread} store={store} i18n={i18n} peopleWithUser={peopleWithUser} />
      </ul>
    </div>
  )
}

function AssignMenuItems({ thread, store, i18n, peopleWithUser }) {
  const responsible = thread.responsible
  return (
    <>
      <li>
        <button type="button" class="dropdown-item text-muted small"
           onClick={() => { store.assignResponsible(thread.id, null) }}>
          {i18n?.t('emails.unassign')}
        </button>
      </li>
      <li><hr class="dropdown-divider my-1" /></li>
      {peopleWithUser.map(person => (
        <li key={person.id}>
          <button type="button"
            class={`dropdown-item d-flex align-items-center gap-2 ${responsible?.id === person.id ? 'fw-semibold' : ''}`}
            onClick={() => { store.assignResponsible(thread.id, person.id) }}
          >
            <PersonAvatar avatarUrl={person.avatar_url} name={person.name} size={24} />
            <span class="small">{person.name}</span>
            {responsible?.id === person.id && <i class="bi bi-check ms-auto text-primary" />}
          </button>
        </li>
      ))}
    </>
  )
}

function AssignOverflowSection({ thread, store, i18n, peopleWithUser }) {
  return (
    <>
      <li><hr class="dropdown-divider my-1" /></li>
      <li><span class="dropdown-item-text text-muted small">{i18n?.t('emails.all_responsible')}</span></li>
      <AssignMenuItems thread={thread} store={store} i18n={i18n} peopleWithUser={peopleWithUser} />
    </>
  )
}

function StarButton({ thread, store, i18n }) {
  const emailRow = store.emails.value.find(e => e.id === thread.id)
  const isStarred = threadUiState(thread, emailRow).starred

  return (
    <button
      class={`btn btn-sm email-thread-btn-icon d-none d-md-inline-flex ${isStarred ? 'email-thread-btn-icon--starred' : ''}`}
      title={i18n?.t('emails.star')}
      onClick={() => store.updateEmailState(thread.id, 'star')}
    >
      <i class={`bi bi-star${isStarred ? '-fill' : ''}`} />
    </button>
  )
}

function StarOverflowItem({ thread, store, i18n }) {
  const emailRow = store.emails.value.find(e => e.id === thread.id)
  const isStarred = threadUiState(thread, emailRow).starred
  return (
    <li>
      <button type="button" class="dropdown-item d-flex align-items-center gap-2 small"
         onClick={() => { store.updateEmailState(thread.id, 'star') }}>
        <i class={`bi bi-star${isStarred ? '-fill text-warning' : ''}`} />
        {i18n?.t(isStarred ? 'emails.unstar' : 'emails.star')}
      </button>
    </li>
  )
}

function MoveFolderDropdown({ thread, store, i18n, folders }) {
  return (
    <div class="dropdown d-none d-md-block">
      <button class="btn btn-sm email-thread-btn-icon dropdown-toggle" data-bs-toggle="dropdown" data-bs-popper-config='{"strategy":"fixed"}' title={i18n?.t('emails.move_to_folder')}>
        <i class="bi bi-folder-symlink" />
      </button>
      <ul class="dropdown-menu shadow-sm">
        <MoveFolderMenuItems thread={thread} store={store} i18n={i18n} folders={folders} />
      </ul>
    </div>
  )
}

function MoveFolderMenuItems({ thread, store, i18n, folders }) {
  return (
    <>
      <li>
        <button type="button" class="dropdown-item d-flex align-items-center gap-2 small"
           onClick={() => { store.moveToFolder(thread.id, null) }}>
          <i class="bi bi-inbox" />
          {i18n?.t('emails.move_to_inbox')}
        </button>
      </li>
      {folders.length > 0 && folders.map(folder => (
        <li key={folder.id}>
          <button type="button"
            class={`dropdown-item d-flex align-items-center gap-2 small ${(thread.folder_id ?? thread.email_folder_id) === folder.id ? 'fw-semibold' : ''}`}
            onClick={() => { store.moveToFolder(thread.id, folder.id) }}
          >
            <i class="bi bi-folder" style={folder.color ? { color: folder.color } : undefined} />
            {folder.name}
            {(thread.folder_id ?? thread.email_folder_id) === folder.id && <i class="bi bi-check ms-auto text-primary" />}
          </button>
        </li>
      ))}
    </>
  )
}

function MoveFolderOverflowSection({ thread, store, i18n, folders }) {
  return (
    <>
      <li><hr class="dropdown-divider my-1" /></li>
      <li><span class="dropdown-item-text text-muted small">{i18n?.t('emails.move_to_folder')}</span></li>
      <MoveFolderMenuItems thread={thread} store={store} i18n={i18n} folders={folders} />
    </>
  )
}

function TagPickerDropdown({ thread, store, i18n }) {
  const mailboxId = thread.mailbox_id
  const allTags = (store.emailTags.value || []).filter(t =>
    mailboxId == null ? true : Number(t.mailbox_id) === Number(mailboxId)
  )
  const appliedTagIds = threadTagIds(thread)
  const [search, setSearch] = useState('')
  const [newTagName, setNewTagName] = useState('')
  const [newTagColor, setNewTagColor] = useState('#6c757d')
  const [creating, setCreating] = useState(false)

  const filtered = search
    ? allTags.filter(t => t.tag.toLowerCase().includes(search.toLowerCase()))
    : allTags

  async function handleCreate(e) {
    e.preventDefault()
    if (!newTagName.trim() || !mailboxId) return
    const name = newTagName.trim()
    const color = newTagColor
    setNewTagName('')
    setNewTagColor('#6c757d')
    setCreating(true)
    await store.createTag(name, color, mailboxId)
    setCreating(false)
  }

  return (
    <div class="dropdown d-none d-md-block">
      <button
        class="btn btn-sm email-thread-btn-icon dropdown-toggle"
        title={i18n?.t('emails.tags_label')}
        data-bs-toggle="dropdown"
        data-bs-popper-config='{"strategy":"fixed"}'
        data-bs-auto-close="outside"
      >
        <i class="bi bi-tag" />
      </button>
      <div class="dropdown-menu shadow-sm p-2" style="min-width:220px">
        <TagPickerPanel
          allTags={allTags}
          filtered={filtered}
          appliedTagIds={appliedTagIds}
          search={search}
          setSearch={setSearch}
          newTagName={newTagName}
          setNewTagName={setNewTagName}
          newTagColor={newTagColor}
          setNewTagColor={setNewTagColor}
          creating={creating}
          handleCreate={handleCreate}
          thread={thread}
          store={store}
          i18n={i18n}
        />
      </div>
    </div>
  )
}

function TagPickerPanel({
  allTags, filtered, appliedTagIds, search, setSearch,
  newTagName, setNewTagName, newTagColor, setNewTagColor,
  creating, handleCreate, thread, store, i18n,
}) {
  return (
    <>
      {allTags.length > 0 && (
        <input
          type="text"
          class="form-control form-control-sm mb-2"
          placeholder={i18n?.t('emails.tag_search_placeholder')}
          value={search}
          onInput={e => setSearch(e.target.value)}
        />
      )}
      <div style="max-height:200px; overflow-y:auto">
        {filtered.map(tag => (
          <label
            key={tag.id}
            class="dropdown-item d-flex align-items-center gap-2 small rounded py-1 px-2"
            style="cursor:pointer"
          >
            <input
              type="checkbox"
              class="form-check-input mt-0"
              checked={appliedTagIds.includes(Number(tag.id))}
              onChange={() => store.toggleTag(thread.id, tag.id)}
            />
            <span
              class="badge rounded-pill"
              style={{ backgroundColor: tag.color || '#6c757d', color: tagTextColor(tag.color || '#6c757d'), fontSize: '0.7rem' }}
            >
              {tag.tag}
            </span>
          </label>
        ))}
        {allTags.length > 0 && filtered.length === 0 && (
          <div class="text-muted small px-2 py-1">{i18n?.t('emails.no_tags_found')}</div>
        )}
      </div>
      {allTags.length > 0 && <hr class="my-1" />}
      <form class="d-flex gap-1 align-items-center" onSubmit={handleCreate}>
        <input
          type="text"
          class="form-control form-control-sm"
          placeholder={i18n?.t('emails.new_tag')}
          value={newTagName}
          onInput={e => setNewTagName(e.target.value)}
        />
        <input
          type="color"
          class="form-control form-control-sm form-control-color email-tag-color-input"
          value={newTagColor}
          onInput={e => setNewTagColor(e.target.value)}
        />
        <button type="submit" class="btn btn-sm btn-outline-primary flex-shrink-0" disabled={creating}>
          <i class="bi bi-plus" />
        </button>
      </form>
    </>
  )
}

function TagOverflowSection({ thread, store, i18n }) {
  const mailboxId = thread.mailbox_id
  const allTags = (store.emailTags.value || []).filter(t =>
    mailboxId == null ? true : Number(t.mailbox_id) === Number(mailboxId)
  )
  const appliedTagIds = threadTagIds(thread)

  return (
    <>
      <li><hr class="dropdown-divider my-1" /></li>
      <li><span class="dropdown-item-text text-muted small">{i18n?.t('emails.tags_label')}</span></li>
      {allTags.map(tag => (
        <li key={tag.id}>
          <label class="dropdown-item d-flex align-items-center gap-2 small" style="cursor:pointer">
            <input
              type="checkbox"
              class="form-check-input mt-0"
              checked={appliedTagIds.includes(Number(tag.id))}
              onChange={() => store.toggleTag(thread.id, tag.id)}
            />
            <span
              class="badge rounded-pill"
              style={{ backgroundColor: tag.color || '#6c757d', color: tagTextColor(tag.color || '#6c757d'), fontSize: '0.7rem' }}
            >
              {tag.tag}
            </span>
          </label>
        </li>
      ))}
    </>
  )
}

function MobileMoreMenu({ thread, store, i18n, folders }) {
  const mailboxId = thread.mailbox_id
  const allTags = (store.emailTags.value || []).filter(t =>
    mailboxId == null ? true : Number(t.mailbox_id) === Number(mailboxId)
  )
  const appliedTagIds = threadTagIds(thread)
  const emailRow = store.emails.value.find(e => e.id === thread.id)
  const isStarred = threadUiState(thread, emailRow).starred
  const [newTagName, setNewTagName] = useState('')
  const [newTagColor, setNewTagColor] = useState('#6c757d')
  const [creating, setCreating] = useState(false)

  async function handleCreateTag(e) {
    e.preventDefault()
    if (!newTagName.trim() || !mailboxId) return
    const name = newTagName.trim()
    const color = newTagColor
    setNewTagName('')
    setNewTagColor('#6c757d')
    setCreating(true)
    await store.createTag(name, color, mailboxId)
    setCreating(false)
  }

  return (
    <div class="dropdown d-md-none email-thread-toolbar-mobile-more flex-shrink-0">
      <button
        class="btn btn-sm btn-outline-secondary dropdown-toggle"
        data-bs-toggle="dropdown"
        data-bs-popper-config='{"strategy":"fixed"}'
        data-bs-auto-close="outside"
        title={i18n?.t('helpers.more_actions') || 'More actions'}
      >
        <i class="bi bi-three-dots" />
      </button>
      <ul class="dropdown-menu dropdown-menu-end shadow-sm email-thread-toolbar-dropdown-menu">
        <li>
          <button type="button" class="dropdown-item d-flex align-items-center gap-2 small"
             onClick={() => { store.openCompose('reply_all', thread.id) }}>
            <i class="bi bi-reply-all" />
            {i18n?.t('emails.reply_all')}
          </button>
        </li>

        {store.canEdit.value && (
          <>
            <li>
              <button
                type="button"
                class="dropdown-item d-flex align-items-center gap-2 small"
                onClick={() => store.openTaskCreateModal(thread.id)}
              >
                <i class="bi bi-list-task" />
                {i18n?.t('emails.new_task_tooltip')}
              </button>
            </li>
            <li>
              <button
                type="button"
                class="dropdown-item d-flex align-items-center gap-2 small"
                onClick={() => store.openTaskLinkPicker(thread.id)}
              >
                <i class="bi bi-link-45deg" />
                {i18n?.t('emails.link_to_task')}
              </button>
            </li>
          </>
        )}

        <li>
          <button type="button" class="dropdown-item d-flex align-items-center gap-2 small"
             onClick={() => { store.showCommentForm() }}>
            <i class="bi bi-sticky" />
            {i18n?.t('emails.add_internal_note')}
          </button>
        </li>

        <li><hr class="dropdown-divider my-1" /></li>

        <li>
          <button type="button" class="dropdown-item d-flex align-items-center gap-2 small"
             onClick={() => { store.updateEmailState(thread.id, 'star') }}>
            <i class={`bi bi-star${isStarred ? '-fill text-warning' : ''}`} />
            {i18n?.t(isStarred ? 'emails.unstar' : 'emails.star')}
          </button>
        </li>

        <li>
          <button type="button" class="dropdown-item d-flex align-items-center gap-2 small"
             onClick={() => { store.updateEmailState(thread.id, 'unread') }}>
            <i class="bi bi-envelope" />
            {i18n?.t('emails.mark_unread')}
          </button>
        </li>

        <MoveFolderOverflowSection thread={thread} store={store} i18n={i18n} folders={folders} />

        <li><hr class="dropdown-divider my-1" /></li>
        <li><span class="dropdown-item-text text-muted small">{i18n?.t('emails.tags_label')}</span></li>
        {allTags.map(tag => (
          <li key={tag.id}>
            <label class="dropdown-item d-flex align-items-center gap-2 small" style="cursor:pointer">
              <input
                type="checkbox"
                class="form-check-input mt-0"
                checked={appliedTagIds.includes(Number(tag.id))}
                onChange={() => store.toggleTag(thread.id, tag.id)}
              />
              <span
                class="badge rounded-pill"
                style={{ backgroundColor: tag.color || '#6c757d', color: tagTextColor(tag.color || '#6c757d'), fontSize: '0.7rem' }}
              >
                {tag.tag}
              </span>
            </label>
          </li>
        ))}
        <li class="px-2 py-1">
          <form class="d-flex gap-1 align-items-center" onSubmit={handleCreateTag}>
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder={i18n?.t('emails.new_tag')}
              value={newTagName}
              onInput={e => setNewTagName(e.target.value)}
            />
            <input
              type="color"
              class="form-control form-control-sm form-control-color email-tag-color-input"
              value={newTagColor}
              onInput={e => setNewTagColor(e.target.value)}
            />
            <button type="submit" class="btn btn-sm btn-outline-primary flex-shrink-0" disabled={creating}>
              <i class="bi bi-plus" />
            </button>
          </form>
        </li>
      </ul>
    </div>
  )
}
